// Leave quota policy — synced to each employee's start date & tenure.
//
// Confirmed policy (Oct 2026):
//  - Leave YEAR is anchored to the work anniversary (resets each year on the
//    start-date anniversary), not the calendar year.
//  - ลาป่วย (sick, 30) and ลากิจ (personal, 5) are available from day one.
//  - ลาพักร้อน (annual, 10): 0 during probation, full 10 once probation passes
//    (within that same leave year). After the first anniversary probation is
//    always behind, so it is simply 10.

export const DEFAULT_PROBATION_MONTHS = 3;

// base = full-year entitlement; fromDayOne = usable during probation
export const LEAVE_POLICY = {
  'ลาพักร้อน': { base: 10, fromDayOne: false },
  'ลาป่วย':    { base: 30, fromDayOne: true  },
  'ลากิจ':     { base: 5,  fromDayOne: true  },
};

function addMonths(date, m) { const d = new Date(date); d.setMonth(d.getMonth() + m); return d; }
function addYears(date, y)  { const d = new Date(date); d.setFullYear(d.getFullYear() + y); return d; }
function iso(d) { const z = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`; }

// Most recent work-anniversary on/before asOf → current leave cycle [start, end)
export function currentCycle(startDate, asOf = new Date()) {
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return null;
  let cycleStart = addYears(start, asOf.getFullYear() - start.getFullYear());
  if (cycleStart > asOf) cycleStart = addYears(cycleStart, -1);
  return { cycleStart, cycleEnd: addYears(cycleStart, 1) };
}

export function probationEndDate(startDate, probationMonths = DEFAULT_PROBATION_MONTHS) {
  return addMonths(new Date(startDate), probationMonths);
}

// Whole months of tenure between start and asOf
export function tenureMonths(startDate, asOf = new Date()) {
  const s = new Date(startDate);
  if (isNaN(s.getTime())) return 0;
  let m = (asOf.getFullYear() - s.getFullYear()) * 12 + (asOf.getMonth() - s.getMonth());
  if (asOf.getDate() < s.getDate()) m -= 1;
  return Math.max(0, m);
}

// Per-type quota for the current cycle.
export function computeLeaveQuota(startDate, probationMonths = DEFAULT_PROBATION_MONTHS, asOf = new Date()) {
  const cyc = currentCycle(startDate, asOf);
  if (!cyc) return null;
  const probEnd = probationEndDate(startDate, probationMonths);
  const inProbation = asOf < probEnd;
  const types = {};
  for (const [name, p] of Object.entries(LEAVE_POLICY)) {
    const locked = !p.fromDayOne && inProbation;
    types[name] = { total: locked ? 0 : p.base, base: p.base, lockedUntil: locked ? probEnd : null };
  }
  return {
    cycleStart: cyc.cycleStart,
    cycleEnd: cyc.cycleEnd,
    cycleStartISO: iso(cyc.cycleStart),
    cycleEndISO: iso(cyc.cycleEnd),
    probationEnd: probEnd,
    probationEndISO: iso(probEnd),
    inProbation,
    tenureMonths: tenureMonths(startDate, asOf),
    types,
  };
}

// Sum used days for a leave type within [cycleStartISO, cycleEndISO) by startDate.
export function usedInCycle(leaves, typeName, cycleStartISO, cycleEndISO) {
  return (leaves || [])
    .filter((l) => l.type === typeName && (l.status === 'approved' || l.status === 'pending'))
    .filter((l) => (l.startDate || '') >= cycleStartISO && (l.startDate || '') < cycleEndISO)
    .reduce((s, l) => s + (Number(l.days) || 0), 0);
}
